import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Dimmer,
  Grid,
  Input,
  Label,
  Loader,
  Segment,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage } from '../../actions/flash';
import LoadImage from '../../components/LoadImage';
import { titleCase, symbolToSpace } from '../../utils/strings';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

const validFileTypes = ['image/gif', 'image/jpeg', 'image/png'];

class UploadLogoForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    t: PropTypes.func,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      uploads: [],
      logoLabels: [],
    };
    this.uploadFiles = null;
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  submit(url, opts = {}) {
    const { store } = this.context;
    return this.setLoading(true)
      .then(() => store.dispatch(fetchM(url, opts)))
      .then(res => res.json())
      .then(result => this.setLoading(false)
        .then(() => this.resetFiles())
        .then(() => result))
      .catch(err => this.setLoading(false)
        .then(() => this.resetFiles())
        .then(() => Promise.reject(err)));
  }
  resetFiles() {
    // temporary fix for upload modal not closing
    this.setState({
      uploads: [],
      logoLabels: [],
    });
  }
  handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(this.form);
    this.state.uploads.forEach((upload, idx) => {
      formData.append(`files_${idx}`, upload.file);
      formData.append(`labels_${idx}`, this.state.logoLabels[idx]);
    });
    if (this.props.onSubmit) {
      this.props.onSubmit(event, formData);
    }
  }
  handleRemove = (event, idx) => {
    event.preventDefault();
    const { uploads, logoLabels } = this.state;
    uploads.splice(idx, 1);
    logoLabels.splice(idx, 1);
    this.setState({ uploads, logoLabels });
  }
  handleAddFile = (event) => {
    event.preventDefault();
    this.uploadFiles.click();
  }
  handleCheckChanges() {
    if (!this.state.hasChanges) {
      this.setState({ hasChanges: true }, () => {
        if (this.props.onChange) {
          this.props.onChange(true);
        }
      });
    }
  }
  handleOnChange = () => {
    const files = Array.prototype.slice.call(this.uploadFiles.files);
    this.uploadFiles.value = null;
    if (window.FileReader) {
      files.forEach((file) => {
        const reader = new FileReader();
        if (!validFileTypes.some(fileType => file.type === fileType)) {
          const { store } = this.context;
          store.dispatch(flashErrorMessage({ message: 'Invalid image file(s) submitted.' }));
          return;
        }
        reader.onloadend = () => {
          const { uploads, logoLabels } = this.state;
          const name = file.name.substring(0, file.name.length - 4);
          uploads.push({
            file,
            name,
            src: reader.result,
          });
          const label = titleCase(symbolToSpace(name));
          logoLabels.push(label);
          this.setState({
            uploads,
            logoLabels,
          }, () => {
            this.handleCheckChanges();
          });
        };
        reader.readAsDataURL(file);
      });
    }
  }
  handleLabelChange = (e, value, idx) => {
    const { logoLabels } = this.state;
    const newValue = value.replace(/[\\~#%&*{}/:<>?|"]/, '');
    logoLabels[idx] = newValue;
    this.setState({ logoLabels });
  }
  render() {
    const { t } = this.props;
    return (
      <form
        encType="multipart/form-data"
        onSubmit={this.handleSubmit}
        name="uploadLogo"
        ref={(ref) => { this.form = ref; }}
      >
        <input
          ref={(ref) => { this.uploadFiles = ref; }}
          type="file"
          onChange={() => this.handleOnChange()}
          accept="image/gif, image/jpeg, image/png"
          multiple
          style={{ display: 'none' }}
        />
        <Grid verticalAlign="middle" stackable>
          <Grid.Row columns="equal">
            <Grid.Column only="computer" />
            <Grid.Column>
              <Grid verticalAlign="middle" columns="equal">
                <Grid.Column>
                  <Button
                    fluid
                    compact
                    primary
                    icon="add"
                    content={t('Logos')}
                    onClick={this.handleAddFile}
                    disabled={this.state.loading}
                  />
                </Grid.Column>
                <Grid.Column>
                  <Button
                    fluid
                    compact
                    type="submit"
                    positive
                    content={t('Upload', { ns: TRANSLATION_FILES.translation })}
                    icon="cloud upload"
                    loading={this.state.loading}
                    disabled={this.state.uploads.length < 1 || this.state.loading}
                  />
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid stackable doubling columns={2}>
          <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
            <Loader indeterminate inline="centered">{t('Uploading', { ns: TRANSLATION_FILES.translation })}</Loader>
          </Dimmer>
          {
            this.state.uploads.length > 0 ?
              this.state.uploads.map((upload, idx) => (
                <Grid.Column key={idx}>
                  <Segment.Group>
                    <Segment secondary>
                      <LoadImage
                        fluid
                        src={upload.src}
                      />
                      <Label
                        as="a"
                        corner
                        color="red"
                        icon="delete"
                        disabled={this.state.loading}
                        onClick={e => this.handleRemove(e, idx)}
                      />
                    </Segment>
                    <Segment padded>
                      <Grid columns={1}>
                        <Grid.Column>
                          <Input
                            label={t('Label')}
                            fluid
                            placeholder={t('Label')}
                            type="text"
                            value={this.state.logoLabels[idx]}
                            disabled={this.state.loading}
                            onChange={(e, { value }) => this.handleLabelChange(e, value, idx)}
                          />
                        </Grid.Column>
                      </Grid>
                    </Segment>
                  </Segment.Group>
                </Grid.Column>
              ))
            : null
          }
        </Grid>
      </form>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(
  connect(UploadLogoForm.mapStateToProps, null, null, { withRef: true })(UploadLogoForm),
);



// WEBPACK FOOTER //
// ./src/containers/Logos/Form.jsx